import React, { useState, useEffect } from 'react';
import { FaUser, FaBookmark, FaEye, FaPen, FaEllipsisV } from 'react-icons/fa';
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link , useNavigate} from "react-router-dom";
import axios from 'axios'; 
import Swal from "sweetalert2";
import { url } from "../../data/url";

const Dashboard = () => {
 
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url.path}/employer/dashboard`, { 
        withCredentials: true });
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      if(error.response && error.response.status === 401) {
        navigate('/login');
      }
      console.error('Error fetching dashboard data:', error);
      setError('Error fetching dashboard data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="dashboard">
      <header>
        <h1>Dashboard</h1>
        <Link to="/employer/post-job" className="add-job-btn">
          <FontAwesomeIcon icon={faPlus} className="icon" />
          <span>New Internship</span>
        </Link>
      </header>
      
      <main>
        <div className="overview-interviews-container">
          <Overview data={dashboardData.overview} />
          <UpcomingInterviews interviews={dashboardData.upcomingInterviews} />
        </div>
        <RecentlyPostedJobs jobs={dashboardData.recentlyPostedJobs} fetchDashboardData={fetchDashboardData} />
      </main>
    </div>
  );
};

const Overview = ({ data }) => {
  const overviewData = [
    { icon: <FaUser />, count: data.postedInternships, label: 'Posted Internships' },
    { icon: <FaPen />, count: data.hired, label: 'Hired' },
    { icon: <FaEye />, count: data.applications, label: 'Applications' },
    { icon: <FaBookmark />, count: data.savedCandidates, label: 'Saved Candidates' },
  ];

  return (
    <section className="overview">
      <h2>Overview</h2>
      <div className="overview-grid">
        {overviewData.map((item, index) => (
          <div key={index} className="overview-item">
            <div className="icon-wrapper">{item.icon}</div>
            <div className="item-details">
              <span className="count">{item.count}</span>
              <span className="label">{item.label}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const UpcomingInterviews = ({ interviews }) => {
  const [activeTab, setActiveTab] = useState('Today');
  const [displayedInterviews, setDisplayedInterviews] = useState([]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    switch (activeTab) {
      case 'Today':
        setDisplayedInterviews(interviews.filter(interview => interview.date === today));
        break;
      case 'Upcoming':
        setDisplayedInterviews(interviews.filter(interview => interview.date > today));
        break;
      case 'Completed':
        setDisplayedInterviews(interviews.filter(interview => interview.date < today));
        break;
      default:
        setDisplayedInterviews(interviews);
    }
  }, [activeTab, interviews]);

  return (
    <div className="interview-container">
      <h2>Upcoming Interviews</h2>
      <section className="upcoming-interviews">
        <div className="interview-tabs">
          <button 
            className={activeTab === 'Today' ? 'active' : ''}
            onClick={() => setActiveTab('Today')}
          >
            Today
          </button>
          <button 
            className={activeTab === 'Upcoming' ? 'active' : ''}
            onClick={() => setActiveTab('Upcoming')}
          >
            Upcoming
          </button>
          <button 
            className={activeTab === 'Completed' ? 'active' : ''}
            onClick={() => setActiveTab('Completed')}
          >
            Completed
          </button>
        </div>
        <ul className="interview-list">
          {displayedInterviews.length > 0 ? (displayedInterviews.map((interview, index) => (
            <li key={index} className="interview-item">
              <div className="interview-info">
                <span className="name">{interview.name}</span>
                <span className="position">For {interview.position}</span>
              </div>
              <span className="time">At {interview.time}</span>
            </li>
          ))) : <div className="interview-placeholder">
            No interviews scheduled
          </div>}
        </ul>
      </section>
    </div>
  );
};

const RecentlyPostedJobs = ({ jobs, fetchDashboardData }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  const toggleDropdown = (jobId) => {
    setActiveDropdown(activeDropdown === jobId ? null : jobId);
  };

  const handleEditJob = (job) => {
    navigate(`/employer/edit-job/${job}`);
  };

  const handlePreviewJob = (job) => {
    navigate(`/employer/internship/${job}/description`);
  };

  const handleDeleteJob = async (job) => {
    console.log("job :", job)
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${url.path}/employer/internship/${job}`);
        fetchDashboardData();
        Swal.fire("Deleted!", "The job has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting job:", error);
        Swal.fire("Error", "There was an error deleting the job.", "error");
      }
    }
  };

  const formatDate = (dateString) => {
    if (dateString === 'Not specified') return 'Not specified';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <section className="recently-posted-jobs">
      <h2>Recently Posted Internships</h2>
      <div className="jobs-list">
        {jobs.length > 0 ? (jobs.map((job, index) => (
          <div key={index} className="job-item">
            <div className="job-info">
              <h3 className="job-title"><strong>{job.title}</strong></h3>
              <p className="job-location">{job.location}</p>
              <p className="job-dates">
                <strong>Created on: </strong>{formatDate(job.created)}&nbsp; &nbsp;&nbsp;
                <strong>Expires: </strong>{formatDate(job.expires)}
              </p>
            </div>
            <div className="job-stats">
              {Object.entries(job.stats).map(([key, value]) => (
                <div key={key} className="stat-item">
                  <span className="stat-label">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                  <span className="stat-value">{value}</span>
                </div>
              ))}
            </div>
            <div className="job-actions">
              {/* <button className="view-applicants-btn">View Applicants</button> */}
              <div
                className="dropdown"
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button
                  className="manage-button"
                  onClick={() => toggleDropdown(job.id)}
                >
                  <FaEllipsisV />
                </button>
                {activeDropdown === job.id && (
                  <div className="dropdown-menu upward">
                    <button className="dropdown-item" onClick={() => handleEditJob(job.id)}>
                      <span className="icon">✏️</span> Edit job
                    </button>
                    <button className="dropdown-item" onClick={() => handlePreviewJob(job.id)}>
                      <span className="icon">👁️</span> Preview job
                    </button>
                    <button className="dropdown-item" onClick={() => handleDeleteJob(job.id)}>
                      <span className="icon">🗑️</span> Permanently delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))) : <div className="internship-placeholder">
        No internship posted yet
      </div>}
      </div>
    </section>
  );
};

export default Dashboard;